// The only place this module talks to the marketing-analytics Units Sold read
// API (`marketing_analytics/units_sold/read_api.py`, merged as PR #88). Every
// response is typed as the S8 envelope, so `meta` is never optional — a
// component that could render without freshness metadata does not typecheck.
//
// The portal is not a backend (I-4). Nothing here recomputes a figure: the
// producer owns every GCS object key and every number in it, and this module
// only reads what the artefact already says. The one shaping step is
// `pluginFrom` below, which UNWRAPS the producer's multi-plugin overview
// envelope — reading a field, not deriving one.

import type { Band, Enveloped, Region } from "@/lib/meteoric-regions";

// The S8 envelope, the region set and the band vocabulary are shared with the
// other UK / EU / MENA module, so they live in one place.
export { isRegion, REGIONS } from "@/lib/meteoric-regions";
export type { Band, Enveloped, FreshnessMeta, Region } from "@/lib/meteoric-regions";

/**
 * Units Sold's severity vocabulary is `warn`, NOT `warning`.
 *
 * Profit Yield's producer emits `warning` for the same idea. Copying that
 * spelling here would silently drop every mid-severity row through the type
 * guard and leave the surface claiming there was nothing to look at.
 */
export type AttentionSeverity = "critical" | "warn" | "info";

/**
 * One dated point of a series.
 *
 * `present: false` marks a period the source query had no row for — the value is
 * zero-filled, not observed. Charting a zero-fill as if it were a measurement is
 * how a gap in collection reads as a collapse in sales, so every consumer here
 * drops non-present points rather than plotting them.
 */
export type SeriesPoint = { m: string; v: number; present: boolean };

export type Product = {
  name: string;
  units: number;
  wow_pct: number | null;
  mom_pct: number | null;
  yoy_pct: number | null;
  top_region?: string | null;
};

export type WindowTotal = {
  units: number;
  wow_pct: number | null;
  mom_pct: number | null;
  yoy_pct: number | null;
};

/** One rolling window (7 / 30 / 90 days) as the producer computed it. */
export type RollingWindow = {
  value: number | null;
  delta_vs_prior_period: number | null;
  band: Band;
  period: string | null;
  products: Product[];
  total: WindowTotal | null;
  total_all?: WindowTotal | null;
};

export type Tile = {
  kpi_id: string;
  kpi_name: string;
  value: number | null;
  unit: string;
  delta_vs_prior_period: number | null;
  band: Band;
  period: string | null;
  sparkline_values: number[];
  series: SeriesPoint[];
  series_daily: SeriesPoint[];
  series_weekly: SeriesPoint[];
  series_monthly: SeriesPoint[];
  // The absolute-units series, parallel to the YoY series above: same point
  // shape, but `v` is a unit COUNT. A payload written before the 2026-07-31
  // producer upgrade simply omits these, which parses to an empty array —
  // absence must never throw and must never fall back to the YoY series.
  series_units_daily: SeriesPoint[];
  series_units_weekly: SeriesPoint[];
  series_units_monthly: SeriesPoint[];
  /** The producer nests the rolling-window map INSIDE the tile, not beside it. */
  windows: Record<string, RollingWindow>;
};

export type AttentionItem = {
  severity: AttentionSeverity;
  headline: string;
  deep_link: string;
};

/** One plugin object out of the overview envelope — the Units Sold one. */
export type UnitsSoldPlugin = {
  tile: Tile;
  products: Product[];
  attention_items: AttentionItem[];
};

/**
 * The RAW overview object. It is a multi-plugin envelope, not the Units Sold
 * payload: `/overview` returns whatever the daily cron wrote, and Units Sold is
 * one entry in `plugins`. The distinction matters because an envelope with an
 * EMPTY `plugins` is a real, meaningful state — the aggregation ran with the
 * plugin switched off — and it is not an error.
 */
export type OverviewEnvelope = {
  version?: string;
  generated_at: string | null;
  age_seconds: number | null;
  plugins: unknown[];
};

export type RegionDetail = {
  generated_at: string | null;
  region: string;
  tile: Tile;
  products: Product[];
};

/** Per-region latest data date, from `/freshness`. */
export type Freshness = Record<string, string | null>;

export type TtuFilter = {
  product: string | null;
  marketplaces: string[];
  source: string | null;
  range: string;
  grain: string;
  region: string;
};

export type TtuScope = {
  marketplaces: Array<[string, string]>;
  excluded_channel: string | null;
  note: string | null;
};

export type TtuStep = {
  value: number | null;
  unit: string;
  measured: boolean;
  data_through: string | null;
  lagging_source?: string | null;
  wow_pct?: number | null;
  mom_pct?: number | null;
  yoy_pct?: number | null;
  basis?: string | null;
  low_confidence?: boolean;
};

export type TtuAttention = {
  severity: AttentionSeverity;
  headline: string;
  step: "traffic" | "conversion" | "units";
  units_effect: number;
  deep_link: string;
};

export type TtuSummary = {
  filter: TtuFilter;
  scope: TtuScope;
  steps: {
    traffic: TtuStep;
    conversion: TtuStep;
    units_per_order: TtuStep;
    units: TtuStep;
  };
  contribution: {
    shares: { traffic: number; conversion: number; units_per_order: number } | null;
    mover: "traffic" | "conversion" | "units_per_order" | null;
    units_yoy_pct: number | null;
    reason: string | null;
  };
  attention: TtuAttention[];
  generated_at: string | null;
};

export type TtuProduct = {
  name: string;
  value: number | null;
  wow_pct: number | null;
  mom_pct: number | null;
  yoy_pct: number | null;
  top_marketplace: string | null;
  low_confidence: boolean;
};

export type TtuSeries = { daily: SeriesPoint[]; weekly: SeriesPoint[]; monthly: SeriesPoint[] };

export type TtuBreakout = {
  filter: TtuFilter;
  scope: TtuScope;
  step: "traffic" | "conversion" | "units";
  headline: TtuStep;
  series_yoy: TtuSeries;
  series_abs: TtuSeries;
  products: TtuProduct[];
  attention: TtuAttention[];
  generated_at: string | null;
};

export type TtuCatalog = {
  products: string[];
  marketplaces: Array<{ channel: "Amazon" | "Shopify"; marketplace: string; region: Region; has_traffic: boolean }>;
  sources: Array<{ key: string; channel: "Amazon" | "Shopify"; sessions: number }>;
  data_through: Record<string, string | null>;
};

// Same-origin via the Pages Function proxy (functions/meteoric-units-sold/[[path]].ts),
// following the routing decision that everything goes through os.ketosource.co
// (Damien, 2026-09-02) — no second hostname, no CORS preflight, no cookie config.
const BASE = import.meta.env.VITE_UNITS_SOLD_API_BASE ?? "/meteoric-units-sold/v1";

export class UnitsSoldError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`Units Sold API ${status}`);
  }
}

async function safeBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function get<T>(path: string): Promise<Enveloped<T>> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new UnitsSoldError(response.status, await safeBody(response));
  }
  return (await response.json()) as Enveloped<T>;
}

export const getOverview = () => get<OverviewEnvelope>("/overview");

export const getRegion = (region: Region) => get<RegionDetail>(`/regions/${region}`);

export const getFreshness = () => get<Freshness>("/freshness");

const ttu = <T>(path: string, query = "") => get<T>(`/traffic-to-units${path}${query ? `?${query}` : ""}`);

export const getSummary = (query: string) => ttu<TtuSummary>("/summary", query);
export const getBreakout = (step: "traffic" | "conversion", query: string) =>
  ttu<TtuBreakout>(`/${step}`, query);
export const getUnits = (query: string) => ttu<TtuBreakout>("/units", query);
export const getCatalog = (range: string) => ttu<TtuCatalog>("/catalog", `range=${encodeURIComponent(range)}`);
export const getTtuFreshness = () => ttu<Record<string, string | null>>("/freshness");

/**
 * The Units Sold plugin out of the overview envelope, or `null` when the
 * producer wrote no such plugin.
 *
 * `null` is returned rather than thrown because "the aggregation ran and Units
 * Sold was switched off" is a true statement the surface can make, and it is a
 * different fact from "we could not read the artefact". Collapsing the two
 * would page someone about a deliberate configuration.
 */
export function pluginFrom(envelope: OverviewEnvelope): UnitsSoldPlugin | null {
  const found = (envelope.plugins ?? []).find(
    (entry): entry is Record<string, unknown> =>
      typeof entry === "object" && entry !== null && (entry as { id?: unknown }).id === "units-sold",
  );
  if (!found) return null;
  return {
    tile: found.tile as Tile,
    products: (found.products as Product[]) ?? [],
    attention_items: (found.attention_items as AttentionItem[]) ?? [],
  };
}

/**
 * One message per API failure. A 404 here is a genuine statement — the daily
 * aggregation has not produced this artefact yet — and is deliberately NOT
 * folded into "something went wrong": "not computed yet" and "we could not
 * read it" are different facts, and only one of them is worth paging someone
 * about.
 *
 * A non-`UnitsSoldError` (a network failure, an unconfigured proxy) degrades to
 * the same stated-unknown as a 503 — never to a blank or an empty chart, which
 * would assert the false claim "there is nothing".
 *
 * `areaName` comes from the registry via the shell (I-5). Hardcoding "Customer
 * Acquisition" would leave the 403 naming the wrong area the day this module is
 * reassigned, so the name is not a literal a module is allowed to hold.
 */
export function messageFor(error: unknown, areaName: string): string {
  if (!(error instanceof UnitsSoldError)) {
    return "Units Sold could not be read, so nothing is shown. Try again shortly.";
  }
  switch (error.status) {
    case 400:
      return invalidFilterMessage(error.body) ?? "That filter is not one this dashboard reports on.";
    case 401:
      return "Your session could not be confirmed. Reload the page to sign in again.";
    case 403:
      return `You do not have permission to see this. Ask the ${areaName} area owner.`;
    case 404:
      return "Units Sold has not been computed yet. The first daily aggregation has not run.";
    case 502:
      return "The analytics store could not be read — credentials, throttling, or a transient error, not a missing dataset. Retry shortly.";
    case 503:
      return "Units Sold is not configured on the server, so nothing is shown.";
    default:
      return "Units Sold could not be read, so nothing is shown. Try again shortly.";
  }
}

function invalidFilterMessage(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const value = body as { error?: unknown; detail?: unknown };
  const nested = value.detail && typeof value.detail === "object" ? value.detail as { error?: unknown; code?: unknown; value?: unknown; detail?: unknown } : null;
  const code = typeof value.error === "string" ? value.error : typeof nested?.error === "string" ? nested.error : typeof nested?.code === "string" ? nested.code : null;
  const rawDetail = nested?.value ?? nested?.detail ?? value.detail;
  const detail = typeof rawDetail === "string" ? rawDetail : JSON.stringify(rawDetail ?? "");
  const kind = code?.replace(/^invalid_/, "") ?? "";
  const unknownPrefix = `unknown ${kind}: `;
  const got = detail.match(/\bgot\s+(['"])(.+)\1\s*$/);
  const extracted = detail.startsWith(unknownPrefix) ? detail.slice(unknownPrefix.length) : got?.[2] ?? detail;
  const messages: Record<string, string> = {
    invalid_product: `The product ${extracted} is not in this catalog.`,
    invalid_marketplace: `The marketplace ${extracted} is not in this catalog.`,
    invalid_source: `The source ${extracted} is not in this catalog.`,
    invalid_range: "That range is not supported.",
  };
  return code ? messages[code] ?? null : null;
}
